import { gql } from 'apollo-boost';
import { client } from '@/lib/api';
import { preventError } from '@/utils/';
import State from '@/store/state';
import {
  ApprovalRequest,
  ApprovalRequestFilter,
  ApproveActionMutation,
  ApproveActionMutationVariables,
  GetApprovalRequestsCountQuery,
  GetApprovalRequestsCountQueryVariables,
  GetApprovalRequestsQuery,
  GetApprovalRequestsQueryVariables,
  PagerInput,
  PagerResponse,
  RejectActionMutation,
  RejectActionMutationVariables,
  SearchInput,
} from '@/generated/graphql';
import { Module } from 'vuex';

type ApprovalRequestsState = State['approvalRequests'];

interface GetApprovalRequestsPayload {
  filter?: ApprovalRequestFilter;
  search?: SearchInput | SearchInput[];
  pager?: PagerInput;
}

export default {
  namespaced: true,

  // -----------------------------------------
  // STATE
  // -----------------------------------------
  state: (): ApprovalRequestsState => ({
    approvalRequests: [],
    approvalRequestsPageInfo: {},
    actionableCount: 0,
  }),

  // -----------------------------------------
  // MUTATIONS
  // -----------------------------------------
  mutations: {
    SET_APPROVAL_REQUESTS_LIST(state: ApprovalRequestsState, payload: ApprovalRequest[]) {
      state.approvalRequests = payload;
    },

    SET_APPROVAL_REQUESTS_PAGEINFO(state: ApprovalRequestsState, payload: PagerResponse) {
      state.approvalRequestsPageInfo = payload;
    },

    SET_ACTIONABLE_COUNT(state: ApprovalRequestsState, count: number) {
      state.actionableCount = count;
    },
  },

  // -----------------------------------------
  // GETTERS
  // -----------------------------------------
  getters: {
    approvalRequestsList(state): ApprovalRequest[] {
      return state.approvalRequests;
    },

    approvalRequestsPageInfo(state): PagerResponse {
      return state.approvalRequestsPageInfo;
    },

    hasActionableRequests(state): boolean {
      return state.actionableCount > 0;
    },
  },

  // -----------------------------------------
  // ACTIONS
  // -----------------------------------------
  actions: {
    // ================================
    // GET APPROVAL REQUESTS LIST
    // ================================
    async GET_APPROVAL_REQUESTS({ commit }, payload: GetApprovalRequestsPayload = {}) {
      const input = {
        filter: payload.filter ?? null,
        pager: payload.pager,
        search: Array.isArray(payload.search) ? payload.search : payload.search ? [payload.search] : undefined,
      };

      try {
        const response = await client.query<GetApprovalRequestsQuery, GetApprovalRequestsQueryVariables>({
          query: gql`
            query GetApprovalRequests($input: ApprovalRequestsInput) {
              approvalRequests(input: $input) {
                items {
                  id
                  actionType
                  status
                  requestedBy {
                    id
                    name
                    email
                  }
                  approvedBy {
                    id
                    name
                    email
                  }
                  rejectedBy {
                    id
                    name
                    email
                  }
                  rejectionReason
                  payload
                  expiresAt
                  usedAt
                  createdAt
                }
                pager {
                  count
                  offset
                  limit
                }
              }
            }
          `,
          variables: { input },
        });

        commit('SET_APPROVAL_REQUESTS_LIST', response.data.approvalRequests.items);
        commit('SET_APPROVAL_REQUESTS_PAGEINFO', response.data.approvalRequests.pager);

        return response.data.approvalRequests;
      } catch (e: any) {
        preventError(e);
      }
    },

    // ================================
    // FETCH ACTIONABLE COUNT (for sidebar badge)
    // ================================
    async FETCH_ACTIONABLE_COUNT({ commit }) {
      try {
        const response = await client.query<GetApprovalRequestsCountQuery, GetApprovalRequestsCountQueryVariables>({
          query: gql`
            query GetApprovalRequestsCount($input: ApprovalRequestsInput) {
              approvalRequests(input: $input) {
                pager {
                  count
                }
              }
            }
          `,
          variables: {
            input: {
              filter: ApprovalRequestFilter.Active,
              pager: { limit: 1, offset: 0 },
            },
          },
          fetchPolicy: 'network-only',
        });

        commit('SET_ACTIONABLE_COUNT', response.data.approvalRequests.pager.count ?? 0);
      } catch {
        // silently ignore — badge is non-critical
      }
    },

    // ================================
    // APPROVE ACTION
    // ================================
    async APPROVE_ACTION(_, id: string) {
      try {
        const response = await client.mutate<ApproveActionMutation, ApproveActionMutationVariables>({
          mutation: gql`
            mutation ApproveAction($id: ID!) {
              approveAction(id: $id) {
                id
                status
              }
            }
          `,
          variables: { id },
        });

        return response.data.approveAction;
      } catch (e: any) {
        preventError(e);
        throw e;
      }
    },

    // ================================
    // REJECT ACTION
    // ================================
    async REJECT_ACTION(_, payload: { id: string; reason?: string }) {
      try {
        const response = await client.mutate<RejectActionMutation, RejectActionMutationVariables>({
          mutation: gql`
            mutation RejectAction($id: ID!, $reason: String) {
              rejectAction(id: $id, reason: $reason) {
                id
                status
              }
            }
          `,
          variables: { id: payload.id, reason: payload.reason },
        });

        return response.data.rejectAction;
      } catch (e: any) {
        preventError(e);
        throw e;
      }
    },
  },
} satisfies Module<ApprovalRequestsState, State>;
